// A refusal, named. When a route guard turns someone away it lands them on a
// fallback page with `?reason=…&from=…&needs=…` — this block is what turns
// those parameters into a sentence, on every fallback page, and into a
// journal row.
//
// Why a persistent notice and not a toast: a refusal is a fact about what the
// person may do, and it has to survive long enough to be read, name the page
// they asked for, the role it needs and the role they hold, and offer the way
// on (apply, switch hat, go back). The platform law (AU-REFUSE): a refusal
// names the rule, is journaled, changes nothing, and is shown — never an empty
// screen or a silent redirect. Before this block the reason was consumed by
// one page as a four-second generic toast, and by every other fallback not at
// all.
//
// Reasons:
//   insufficient_role   the account does not hold a role the page needs
//   persona_restricted  the account holds it but is viewing as another role
//   desk_restricted     a staff account whose role does not sit at the desk
//                       that works the page; `needs` carries desk keys
//
// Mounted once in Layout and once in GuestLayout, so any fallback shows it.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext.jsx';
import { audit } from '../../api/audit.js';
import { Text } from '../elements/text.element.jsx';
import { Button } from '../elements/Button.jsx';
import { deskLabels, desksForRole, ANY_DESK } from '@moksy/common/src/permissions/desks.js';

//   identity_changed    this tab's token and its identity stamp disagreed —
//                       another sign-in on this browser shadowed it — so the
//                       tab was signed out here, and only here
const REASONS = new Set(['insufficient_role', 'persona_restricted', 'identity_changed', 'desk_restricted']);
const ROLE_WORDS = {
  user: 'a member',
  member: 'a member',
  customer: 'a paid customer',
  guest: 'a guest',
  owner_group: 'a staff account',
  admin: 'an admin',
  super_admin: 'a super admin',
  manager: 'a manager',
  finance: 'a finance manager',
  support: 'a support agent',
  reviewer: 'an approved reviewer',
  lead_reviewer: 'a lead reviewer',
  agency: 'an agency account',
  owner: 'a company owner'
};
const roleWord = (r) => ROLE_WORDS[String(r || '').toLowerCase()] || `the ${String(r || '').replace(/_/g, ' ')} role`;
const list = (xs) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}` : xs[0]);

export const refusalText = ({ reason, from, needs, as, held }) => {
  const page = from ? `That page (${from})` : 'That page';
  const needed = needs.length ? list(needs.map(roleWord)) : 'a role this account does not hold';
  if (reason === 'identity_changed') {
    return `${page} was open as one person while this browser signed in as another, so this tab was signed out. Nothing was sent as the other person. Sign in again to continue.`;
  }
  if (reason === 'desk_restricted') {
    const wanted = deskLabels(needs);
    const mine = deskLabels(desksForRole(held).filter((d) => d !== ANY_DESK));
    const desk = wanted.length ? `the ${list(wanted)} desk${wanted.length > 1 ? 's' : ''}` : 'a desk this account does not sit at';
    const at = mine.length ? ` Your role sits at ${list(mine)}.` : '';
    return `${page} belongs to ${desk}.${at} Ask the person who seats staff to add the desk if you need it. Nothing on your account has changed.`;
  }
  if (reason === 'persona_restricted') {
    return `${page} is for ${needed}. You hold it, but you are viewing as ${as || 'another role'}. Switch back with “Test as…” in the header.`;
  }
  return `${page} is for ${needed}. You are signed in as ${held ? roleWord(held) : 'a member'}. Nothing on your account has changed.`;
};

export const RefusalNotice = () => {
  const [params, setParams] = useSearchParams();
  const { user, activeRole } = useAuthContext();
  const [dismissed, setDismissed] = useState(false);
  const reason = String(params.get('reason') || '').toLowerCase();
  const from = String(params.get('from') || '');
  const needs = String(params.get('needs') || '').split(',').map((s) => s.trim()).filter(Boolean);
  const as = String(params.get('as') || '');
  const held = activeRole || user?.role || '';
  const show = REASONS.has(reason) && !dismissed;

  const text = useMemo(() => refusalText({ reason, from, needs, as, held }), [reason, from, needs.join(','), as, held]);

  // Journal the refusal ONCE per refusal. The server never saw a request (the
  // guard is client-side), so this is the only row that says it happened. The
  // key is the cause — this landing, not the clock — held in a ref so React's
  // development double-effect and a re-render do not write a second row.
  // Best effort: a failed journal write must not hide the notice.
  const journaled = useRef('');
  useEffect(() => {
    if (!show) return;
    const key = `${reason}|${from}|${needs.join(',')}|${as}`;
    if (journaled.current === key) return;
    journaled.current = key;
    audit.events({
      action: 'AUTH.ROUTE_REFUSED',
      actionType: 'security',
      resourceType: 'route',
      resourceId: from || null,
      status: 'refused',
      // The audit service keeps `changes` as {before, after}; anything else is
      // flattened to nulls, which is how the first rows lost the reason.
      changes: { before: null, after: { reason, needs, held: held || null, as: as || null } }
    }).catch(() => {});
  }, [show, reason, from, needs.join(','), held, as]);

  if (!show) return null;

  const dismiss = () => {
    setDismissed(true);
    const next = new URLSearchParams(params);
    for (const k of ['reason', 'from', 'needs', 'as']) next.delete(k);
    setParams(next, { replace: true });
  };
  const wantsReviewer = needs.some((n) => /reviewer/.test(n));

  return (
    <div role="alert" data-testid="refusal-notice" className="border-b border-amber-200 bg-amber-50">
      <div className="mx-auto flex max-w-7xl items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <Text role="eyebrow" className="text-amber-800">Not open to this account</Text>
          <Text role="paragraph" className="mt-0.5 text-amber-900">{text}</Text>
          {wantsReviewer ? (
            <Link to="/reviewer/application" className="mt-1 inline-block text-sm font-medium text-amber-900 underline">Apply to become a reviewer →</Link>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={dismiss}>Dismiss</Button>
      </div>
    </div>
  );
};

export default RefusalNotice;
